import { useState, useMemo, useRef, useEffect, useCallback, lazy, Suspense, memo } from "react";
import { Avatar } from "@heroui/react";
import { useSelector } from "react-redux";
import { HiOutlineClock } from "react-icons/hi2";
import {
  FilledStarIcon,
  StarIcon,
} from "../../../assets/icons";
import { capitalize } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../../../redux/socket/useSocket.js";
import { useUpdatePartnerStatus } from "../../../lib/react-query/partners.ts";

const PartnerDetailsModal = lazy(() =>
  import("./PartnerDetailsModal").then((m) => ({ default: m.PartnerDetailsModal }))
);

const PartnerAvatar = memo(function PartnerAvatar({ src }) {
  return (
    <Avatar
      showFallback
      size="xl"
      color="secondary"
      className="!size-16"
      classNames={{
        base: "bg-mediumgrey/30",
        icon: "text-black/60",
      }}
      src={src}
    />
  );
});

export function PartnerCard({
  src,
  name,
  designation,
  address,
  id,
  handleClearFilter,
  item,
  pending,
}) {
  const { sendCommand } = useSocket();
  const navigate = useNavigate();
  const currentUserId = useSelector((state) => state?.user?.user?.id);
  const [openPartnerModal, setOpenPartnerModal] = useState(false);
  const [status, setStatus] = useState(null);
  const [loadingValue, setLoadingValue] = useState(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [openedFromPath, setOpenedFromPath] = useState("");

  const { mutateAsync: updateStatus, isPending: updateStatusPending } =
    useUpdatePartnerStatus();

  const handleClearFilterRef = useRef(handleClearFilter);
  useEffect(() => {
    handleClearFilterRef.current = handleClearFilter;
  }, [handleClearFilter]);

  const formattedPath = useMemo(() => {
    const lastPart = (openedFromPath || "").split("/").pop() || "";
    return lastPart
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, [openedFromPath]);

  const displayName = useMemo(() => {
    if (!name?.trim()) return "--";
    return name
      .split(" ")
      .map((word) => capitalize(word))
      .join(" ");
  }, [name]);

  const displayDesignation = useMemo(() => {
    if (!designation?.trim()) return "-";
    return designation
      .split(" ")
      .map((word) => capitalize(word))
      .join(" ");
  }, [designation]);

  const displayAddress = useMemo(() => {
    if (!address) return "-";
    return address
      .split(" ")
      .map((word) => capitalize(word))
      .join(" ");
  }, [address]);

  const handleOpen = useCallback(() => {
    if (pending) return;
    setOpenedFromPath(
      typeof window !== "undefined" ? window.location.pathname : ""
    );
    setSelectedPartnerId(id);
    setOpenPartnerModal(true);
  }, [pending, id]);

  const handleCloseModal = useCallback(() => {
    setOpenPartnerModal(false);
    setSelectedPartnerId(null);
  }, []);

  const handleNavigateProfile = useCallback(() => {
    navigate(`/partners/partner-detail/${id}`, {
      state: { partnerName: formattedPath },
    });
  }, [navigate, id, formattedPath]);

  const updatePartnerStatus = useCallback(
    async (statusValue, partnerId, relationshipLabel = "") => {
      try {
        setLoadingValue(statusValue);
        await updateStatus({
          id: partnerId,
          status: statusValue?.toUpperCase(),
          relationship_label: relationshipLabel,
        });
        sendCommand("fetch_channels");
        handleCloseModal();
        handleClearFilterRef.current?.();
      } catch (e) {
        setLoadingValue(null);
      } finally {
        setLoadingValue(null);
      }
    },
    [updateStatus, sendCommand, handleCloseModal]
  );

  const handleStatusChange = useCallback(
    (statusValue, partnerId, sPartnerDetails, relationshipLabel = "") => {
      if (statusValue === "chat" && sPartnerDetails) {
        navigate(`/chat`, {
          state: {
            user_id: partnerId,
            completeItem: {
              avatar: sPartnerDetails?.profile_image || "",
              channel_id: null,
              chatType: "individual",
              id: partnerId,
              name: sPartnerDetails?.full_name || "",
              online: false,
              selected: false,
            },
          },
        });
        return;
      }
      setStatus(statusValue);
      updatePartnerStatus(statusValue, partnerId, relationshipLabel);
    },
    [navigate, updatePartnerStatus]
  );

  const isPrioritized =
    item?.status === "PRIORITIZE" ||
    item?.user?.status?.status === "PRIORITIZE" ||
    item?.partner?.status === "PRIORITIZE" ||
    item?.status?.status === "PRIORITIZE";

  const hideStarIcon =
    (item?.status === "MATCH" && item?.request_status === "ACCEPTED") ||
    (item?.user?.status === "MATCH" &&
      item?.user?.request_status === "PENDING" &&
      item?.user?.type === "requested") ||
    (item?.matched_user?.status === "MATCH" &&
      item?.matched_user?.request_status === "ACCEPTED");

  // Determine which relationship label to show for the logged-in user
  const relationshipLabel = useMemo(() => {
    if (!item || !currentUserId) return null;
    // matched_partners: matched_user is the other user
    if (item?.matched_user) {
      return String(item?.matched_user?.id) === String(currentUserId)
        ? item?.receiver_relationship_label
        : item?.sender_relationship_label;
    }
    // pending_partners / prioritized_partners: partner is the other user
    if (item?.partner) {
      return String(item?.partner?.id) === String(currentUserId)
        ? item?.receiver_relationship_label
        : item?.sender_relationship_label;
    }
    // requested_partners: user is the requester, I am the receiver
    if (item?.user) {
      return item?.receiver_relationship_label;
    }
    return null;
  }, [item, currentUserId]);

  return (
    <>
      <div
        onClick={handleOpen}
        role="button"
        tabIndex={pending ? -1 : 0}
        aria-disabled={pending || undefined}
        aria-label={`View details for ${displayName}`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
        className={`border relative rounded-3xl bg-white hover:shadow-md transition p-4 vertical-center gap-3 group ${
          !pending ? "cursor-pointer" : "cursor-auto"
        } select-none`}
      >
        <PartnerAvatar src={src} />
        <div className="font-sans">
          <p className="text-sm font-semibold">{displayName}</p>
          <p className="text-[13px] text-darkgrey">{displayDesignation}</p>
          <p className="text-xs text-grey">{displayAddress}</p>
          {relationshipLabel ? (
            <p className="text-[11px] text-primary mt-0.5 font-medium truncate max-w-[160px]">
              {relationshipLabel}
            </p>
          ) : null}
        </div>
        {pending ? (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full border border-secondary/30 bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-dark">
            <HiOutlineClock className="size-3" />
            Pending
          </div>
        ) : hideStarIcon ? null : isPrioritized ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleStatusChange("deprioritize", id);
            }}
            className="absolute top-2 right-2 cursor-pointer text-secondary-dark p-2 -m-1 min-w-[36px] min-h-[36px] flex items-center justify-center"
            aria-label="Remove from favorites"
          >
            <FilledStarIcon />
          </button>
        ) : (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleStatusChange("prioritize", id);
            }}
            className="absolute top-2 right-2 cursor-pointer p-2 -m-1 min-w-[36px] min-h-[36px] flex items-center justify-center"
            aria-label="Add to favorites"
          >
            <StarIcon />
          </button>
        )}
      </div>

      {openPartnerModal && (
        <Suspense fallback={null}>
          <PartnerDetailsModal
            open={openPartnerModal}
            onClose={handleCloseModal}
            partnerId={id}
            selectedPartnerId={selectedPartnerId}
            src={src}
            item={item}
            openedFromPath={openedFromPath}
            formattedPath={formattedPath}
            onNavigateProfile={handleNavigateProfile}
            onStatusChange={handleStatusChange}
            loadingValue={loadingValue}
            status={status}
            updateStatusPending={updateStatusPending}
          />
        </Suspense>
      )}
    </>
  );
}
